/**
 * SMB Sync Service
 * Handles SMB vendor cost import workflow
 *
 * Workflow:
 * 1. uploadCatalog() - Upload SMB catalog file (Square/QuickBooks export)
 * 2. uploadVendor() - Upload vendor cost file (processed through smartImport)
 * 3. getStatus() - Poll for vendor processing completion
 * 4. process() - Apply mappings and pricing rules, generate output files
 * 5. chain() - Upload another vendor file for same catalog (optional)
 */

import { apiService, ApiResponse } from './apiService'
import type {
  SmbSyncJob,
  CatalogUploadResponse,
  VendorUploadResponse,
  ProcessRequest,
  ProcessResult,
  SmbPlatform,
  VendorSampleData,
} from '../pages/workflows/smb-sync/types'

/**
 * Polling configuration
 */
interface PollConfig {
  intervalMs?: number // Default: 2000
  maxAttempts?: number // Default: 180 (6 minutes)
}

/**
 * SMB Sync Service Class
 */
export class SmbSyncService {
  private readonly SMB_SYNC_ENDPOINT = '/smb-sync'
  private readonly DEFAULT_POLL_INTERVAL = 2000 // 2 seconds
  private readonly DEFAULT_MAX_ATTEMPTS = 180 // 6 minutes at 2 second intervals

  /**
   * Step 1: Upload SMB catalog file and get presigned URL for S3
   *
   * @param filename - Original filename
   * @param platform - 'square' or 'quickbooks'
   * @param jobId - Optional job ID for resuming or replacing catalog
   * @returns Job ID, presigned PUT URL, and detected columns
   *
   * @example
   * const result = await smbSyncService.uploadCatalog('square-export.csv', 'square')
   */
  async uploadCatalog(
    filename: string,
    platform: SmbPlatform,
    jobId?: string
  ): Promise<ApiResponse<CatalogUploadResponse>> {
    const requestBody = {
      filename,
      platform,
      ...(jobId && { job_id: jobId }),
    }

    return await apiService.post<CatalogUploadResponse>(
      `${this.SMB_SYNC_ENDPOINT}/upload-catalog`,
      requestBody
    )
  }

  /**
   * Step 1b: Trigger catalog file processing
   *
   * Call this AFTER successful S3 upload to detect columns.
   *
   * @param jobId - Job ID from uploadCatalog()
   * @returns Detected catalog columns
   *
   * @example
   * await smbSyncService.uploadToS3(putUrl, file)
   * const result = await smbSyncService.processCatalog(jobId)
   */
  async processCatalog(jobId: string): Promise<ApiResponse<{ catalog_columns: string[] }>> {
    return await apiService.post<{ catalog_columns: string[] }>(
      `${this.SMB_SYNC_ENDPOINT}/process-catalog`,
      { job_id: jobId }
    )
  }

  /**
   * Step 2: Upload vendor cost file and get presigned URL for S3
   *
   * File will be processed through smartImport automatically
   *
   * @param jobId - Job ID from uploadCatalog()
   * @param filename - Original filename
   * @returns Presigned PUT URL
   *
   * @example
   * const result = await smbSyncService.uploadVendor(jobId, 'vendor-costs.xlsx')
   */
  async uploadVendor(jobId: string, filename: string): Promise<ApiResponse<VendorUploadResponse>> {
    const requestBody = {
      job_id: jobId,
      filename,
    }

    return await apiService.post<VendorUploadResponse>(
      `${this.SMB_SYNC_ENDPOINT}/upload-vendor`,
      requestBody,
    )
  }

  /**
   * Step 2b: Trigger vendor file processing (smartImport)
   *
   * Call this AFTER successful S3 upload to trigger processing.
   *
   * For Excel files, this returns tabs list. For CSV files, returns columns directly.
   *
   * @param jobId - Job ID from uploadCatalog()
   * @returns Detected vendor columns (CSV) OR tabs list (Excel)
   *
   * @example
   * await smbSyncService.uploadToS3(putUrl, file)
   * const result = await smbSyncService.processVendor(jobId)
   * if (result.data.tabs) {
   *   // Excel file - show tab selection
   * } else if (result.data.vendor_columns) {
   *   // CSV file - proceed to field mapping
   * }
   */
  async processVendor(
    jobId: string
  ): Promise<ApiResponse<{ vendor_columns?: string[]; tabs?: string[] }>> {
    return await apiService.post<{ vendor_columns?: string[]; tabs?: string[] }>(
      `${this.SMB_SYNC_ENDPOINT}/process-vendor`,
      { job_id: jobId }
    )
  }

  /**
   * Step 2c: Select vendor tabs (Excel files only)
   *
   * Call this AFTER processVendor() returns tabs list.
   * User selects which tabs to process, backend combines them and returns columns.
   *
   * @param jobId - Job ID from uploadCatalog()
   * @param selectedTabs - Array of tab names to process
   * @returns Detected vendor columns from selected tabs
   *
   * @example
   * const result = await smbSyncService.selectVendorTabs(jobId, ['Sheet1', 'Sheet2'])
   */
  async selectVendorTabs(
    jobId: string,
    selectedTabs: string[]
  ): Promise<ApiResponse<{ vendor_columns: string[] }>> {
    return await apiService.post<{ vendor_columns: string[] }>(
      `${this.SMB_SYNC_ENDPOINT}/select-vendor-tabs`,
      { job_id: jobId, selected_tabs: selectedTabs }
    )
  }

  /**
   * Step 2d: Get full sample data with per-table structure
   *
   * Call this AFTER vendor file processing completes (after selectVendorTabs for Excel
   * or after processVendor for CSV files).
   *
   * Returns the complete data.sample structure with per-table headers and sample rows.
   *
   * @param jobId - Job ID from uploadCatalog()
   * @returns Full sample data with table_samples, headers, and preview rows
   *
   * @example
   * const result = await smbSyncService.getSampleData(jobId)
   * if (result.success && result.data) {
   *   const tables = result.data.table_samples // { "Godox-1": { header_values: [...], sample_data_rows: [...] } }
   * }
   */
  async getSampleData(jobId: string): Promise<ApiResponse<VendorSampleData>> {
    return await apiService.get<VendorSampleData>(
      `${this.SMB_SYNC_ENDPOINT}/sample?job_id=${jobId}`
    )
  }

  /**
   * Step 3: Get current job status
   *
   * @param jobId - Job ID from uploadCatalog()
   * @returns Current processing state and detected columns
   *
   * @example
   * const status = await smbSyncService.getStatus(jobId)
   */
  async getStatus(jobId: string): Promise<ApiResponse<SmbSyncJob>> {
    return await apiService.get<SmbSyncJob>(`${this.SMB_SYNC_ENDPOINT}/status/${jobId}`)
  }

  /**
   * Poll job status until catalog is ready
   *
   * @param jobId - Job ID to poll
   * @param config - Polling configuration
   * @returns Final job status with catalog columns
   *
   * @example
   * const status = await smbSyncService.pollUntilCatalogReady(jobId)
   */
  async pollUntilCatalogReady(jobId: string, config?: PollConfig): Promise<ApiResponse<SmbSyncJob>> {
    const intervalMs = config?.intervalMs || this.DEFAULT_POLL_INTERVAL
    const maxAttempts = config?.maxAttempts || this.DEFAULT_MAX_ATTEMPTS

    let attempts = 0

    return new Promise((resolve) => {
      const poll = async () => {
        attempts++

        // Check max attempts
        if (attempts > maxAttempts) {
          resolve({
            success: false,
            message: `Polling timeout after ${maxAttempts} attempts`,
            errors: [
              {
                code: 'POLLING_TIMEOUT',
                message: `Exceeded maximum attempts (${maxAttempts})`,
                details: { attempts, maxAttempts },
              },
            ],
          })
          return
        }

        // Get current status
        const statusResponse = await this.getStatus(jobId)

        if (!statusResponse.success || !statusResponse.data) {
          // Failed to get status - retry
          setTimeout(poll, intervalMs)
          return
        }

        const { state, error } = statusResponse.data

        // Check for target state (success)
        if (state === 'catalog_ready') {
          resolve(statusResponse)
          return
        }

        // Check for error state (failure)
        if (state === 'failed') {
          resolve({
            success: false,
            message: error || 'Catalog processing failed',
            errors: [
              {
                code: 'CATALOG_PROCESSING_FAILED',
                message: error || 'Unknown error',
                details: statusResponse.data,
              },
            ],
          })
          return
        }

        // Still processing - continue polling
        setTimeout(poll, intervalMs)
      }

      // Start polling
      poll()
    })
  }

  /**
   * Step 4: Process files with mappings and pricing rules
   *
   * @param request - Process configuration
   * @returns Processing statistics and output file URLs
   *
   * @example
   * const result = await smbSyncService.process({
   *   job_id: jobId,
   *   catalog_mappings: { token: 'ID', price: 'Price', cost: 'Cost' },
   *   vendor_mappings: { cost: 'Unit Cost' },
   *   pricing_rule: { cost_source: 'cost', price_type: 'formula', multiplier: 1.2 }
   * })
   */
  async process(request: ProcessRequest): Promise<ApiResponse<ProcessResult>> {
    return await apiService.post<ProcessResult>(`${this.SMB_SYNC_ENDPOINT}/process`, request)
  }

  /**
   * Step 5: Chain another vendor file to same catalog (optional)
   *
   * Use after successful processing with zero exceptions
   *
   * @param jobId - Job ID from previous process
   * @param filename - New vendor filename
   * @returns Presigned PUT URL for new vendor file
   *
   * @example
   * const result = await smbSyncService.chain(jobId, 'vendor-costs-2.xlsx')
   */
  async chain(jobId: string, filename: string): Promise<ApiResponse<VendorUploadResponse>> {
    const requestBody = {
      job_id: jobId,
      filename,
    }

    return await apiService.post<VendorUploadResponse>(
      `${this.SMB_SYNC_ENDPOINT}/chain`,
      requestBody
    )
  }

  /**
   * Upload file to S3 using presigned URL
   *
   * @param putUrl - Presigned S3 PUT URL
   * @param file - File to upload
   * @returns Success/failure
   *
   * @example
   * await smbSyncService.uploadToS3(response.put_url, file)
   */
  async uploadToS3(putUrl: string, file: File): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(putUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
      })

      if (!response.ok) {
        return {
          success: false,
          message: `S3 upload failed: ${response.status} ${response.statusText}`,
          errors: [
            {
              code: 'S3_UPLOAD_FAILED',
              message: `HTTP ${response.status}`,
              details: { status: response.status, statusText: response.statusText },
            },
          ],
        }
      }

      return {
        success: true,
        message: 'File uploaded successfully',
      }
    } catch (error: any) {
      return {
        success: false,
        message: `Upload error: ${error.message}`,
        errors: [
          {
            code: 'UPLOAD_ERROR',
            message: error.message,
            details: error,
          },
        ],
      }
    }
  }

  /**
   * Poll job status until vendor is ready (or tabs ready for Excel files)
   *
   * For Excel files: Polling completes when state = 'vendor_tabs_ready' (tabs extracted)
   * For CSV files: Polling completes when state = 'vendor_ready' (columns extracted)
   *
   * @param jobId - Job ID to poll
   * @param config - Polling configuration
   * @returns Final job status with vendor_tabs (Excel) or vendor_columns (CSV)
   *
   * @example
   * const status = await smbSyncService.pollUntilVendorReady(jobId)
   * if (status.data?.vendor_tabs) {
   *   // Excel file - show tab selection
   * } else if (status.data?.vendor_columns) {
   *   // CSV file - proceed to field mapping
   * }
   */
  async pollUntilVendorReady(jobId: string, config?: PollConfig): Promise<ApiResponse<SmbSyncJob>> {
    const intervalMs = config?.intervalMs || this.DEFAULT_POLL_INTERVAL
    const maxAttempts = config?.maxAttempts || this.DEFAULT_MAX_ATTEMPTS

    let attempts = 0

    return new Promise((resolve) => {
      const poll = async () => {
        attempts++

        // Check max attempts
        if (attempts > maxAttempts) {
          resolve({
            success: false,
            message: `Polling timeout after ${maxAttempts} attempts`,
            errors: [
              {
                code: 'POLLING_TIMEOUT',
                message: `Exceeded maximum attempts (${maxAttempts})`,
                details: { attempts, maxAttempts },
              },
            ],
          })
          return
        }

        // Get current status
        const statusResponse = await this.getStatus(jobId)

        if (!statusResponse.success || !statusResponse.data) {
          // Failed to get status - retry
          setTimeout(poll, intervalMs)
          return
        }

        const { state, error, vendor_error } = statusResponse.data

        // Check for target states (success)
        // vendor_tabs_ready = Excel file, tabs extracted, waiting for user selection
        // vendor_ready = CSV file, columns extracted, ready for field mapping
        if (state === 'vendor_tabs_ready' || state === 'vendor_ready') {
          resolve(statusResponse)
          return
        }

        // Check for vendor error (recoverable - can try another file)
        // State goes back to catalog_ready with vendor_error set
        if (state === 'catalog_ready' && vendor_error) {
          resolve({
            success: false,
            message: vendor_error,
            errors: [
              {
                code: 'VENDOR_FILE_ERROR',
                message: vendor_error,
                details: { recoverable: true, state },
              },
            ],
          })
          return
        }

        // Check for error state (failure - not recoverable)
        if (state === 'failed') {
          resolve({
            success: false,
            message: error || 'Vendor processing failed',
            errors: [
              {
                code: 'VENDOR_PROCESSING_FAILED',
                message: error || 'Unknown error',
                details: statusResponse.data,
              },
            ],
          })
          return
        }

        // Still processing - continue polling
        setTimeout(poll, intervalMs)
      }

      // Start polling
      poll()
    })
  }

  /**
   * Download result file from S3 presigned URL
   *
   * @param resultUrl - S3 presigned GET URL from process result
   * @param filename - Suggested filename for download
   *
   * @example
   * await smbSyncService.downloadResultFile(urls.updated, 'updated-products.xlsx')
   */
  async downloadResultFile(resultUrl: string, filename: string): Promise<void> {
    try {
      const response = await fetch(resultUrl)

      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.status} ${response.statusText}`)
      }

      // Create blob and trigger download
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error: any) {
      console.error('Failed to download result file:', error)
      throw error
    }
  }
}

// Export singleton instance
export const smbSyncService = new SmbSyncService()
export default smbSyncService
